import { parseQueryParamsObject } from "@robe/core-utils";
import { BaseApiRequest } from "../baseApiRequest";
import { XcoreCore } from "XcoreCore";
import { Content } from "@robe/core-types";
import { resolveFetchPublishedOnly } from "./utils/queryParamsResolvers";
import { resolveUnfetchedValuesToContentValuesForContents } from "./utils/resolveAndAssignUnfetchedValues";

export interface GetSearchResultOptions {
  page?: number;
  perPage?: number;
  count?: number;
  length?: number;
  fields?: string[];
  locales?: string[];
  publishedOnly?: boolean;
  includeContentData?: boolean;
  includeValues?: boolean;
  searchInTitle?: boolean; // whether to search for a search match only in the title or also in the other fields
  "sort[0].fieldName"?: string;
  "sort[0].orderBy"?: "Ascending" | "Descending";
}

export type ContentSearchContentResult<T extends Content = Content> = Pick<T, "title" | "locales" | "id" | "isSingle" | "type"> &
  Partial<Content>;

export interface ContentSearchResponseObject<T extends Content = Content> {
  currentPage: number;
  pageSize: number;
  /** Number of total items number without pagination */
  totalCount: number;
  /** Number of available pages */
  totalPages: number;
  /** First order number from range of currently fetched contents page */
  from: number;
  /** Last order number from range of currently fetched contents page */
  to: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  items: ContentSearchContentResult<T>[];
}

/** Key is content type */
export type ContentSearchResponse = Record<string, ContentSearchResponseObject>;

/**
 * Fetches search results based on the provided search term, content types, and request context.
 * This function is designed to query the content search API and return a promise resolving to a partial record of content IDs grouped by their types.
 *
 * @typeParam TKey A type extending `string`, representing the keys of the result object, which are the content types.
 *
 * @param core The XcoreCore instance.
 * @param term The search term to query the content search API.
 * @param types (Optional), an array of content types to limit the search results to.
 * @param options.fields (Optional), list of specific content fields to fetch.
 * @param options.publishedOnly (Optional), in web only published contents are being fetched by default
 * if not specified otherwise. In admin all contents are being fetched by default if not specified otherwise.
 * @param options.locales (Optional), fetch only contents with locales.
 * @param options.includeValues (Optional), include all content values in `values` field.
 * Doesn't have any effect if `fields` param is being used.
 * @param options.includeContentData (Optional), include rest of content data.
 * This is controlled on EP by `minimal` param but is set to `false` by default.
 * It is better to have this set to `true` by default, and only fetch additional data if we really need it.
 * Doesn't have any effect if `fields` param is being used.
 * @param options.page (Optional), which page of total pages should be fetched.
 * @param options.perPage (Optional), number of items per page.
 * @param options.count (Optional), max number of contents that should be fetched in single page.
 * @param options.length (Optional), max number of total contents that should be fetched.
 *
 * @returns An `BaseApiRequest` that resolves to a `ContentSearchResponse`, where each key is a content type and the value is an array of contents.
 *
 */
export const getSearchResult = <TKey extends string>(
  core: XcoreCore,
  term: string,
  types?: TKey[],
  { publishedOnly, includeContentData, includeValues, fields, ...options }: GetSearchResultOptions = {}
): BaseApiRequest<ContentSearchResponse> =>
    core
      .fetch(
        `/api/content/search/${term}${parseQueryParamsObject({
          types,
          ...resolveFetchPublishedOnly(publishedOnly),
          ...(fields && fields.length > 0
            ? { fields, includeValues: true, minimal: false }
            : {
              minimal: !includeContentData,
              includeValues: includeValues ?? false,
            }),
          ...options,
        })}`
      )
      .then((res) => res.json())
      .then(async (res: ContentSearchResponse) => {
        const entries = await Promise.all(
          Object.entries(res).map(async ([key, value]) => {
            const resolvedItems = await resolveUnfetchedValuesToContentValuesForContents(value.items, core);
            return [key, { ...value, items: resolvedItems }];
          })
        );

        return Object.fromEntries(entries);
      });
